import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Logo } from "@/components/mdh/Nav";
import { formatApiError } from "@/auth/AuthContext";
import { API, BACKEND } from "@/lib/api";

export default function Reset() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (password !== confirm) {
            setError("Passwords do not match");
            return;
        }
        setBusy(true);
        setError("");
        try {
            const r = await fetch(
                `${BACKEND}/api/auth/reset-password`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ token, password }),
                },
            );
            if (!r.ok) {
                const d = await r.json();
                throw new Error(formatApiError(d.detail));
            }
            setDone(true);
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="reset-page" className="relative flex min-h-screen items-center justify-center px-5">
            <div className="bg-grid-slate pointer-events-none absolute inset-0 opacity-50" aria-hidden="true" />
            <div className="glass-panel relative w-full max-w-md rounded-xl p-8">
                <Logo />
                <h1 className="font-display mt-8 text-2xl font-bold tracking-tight text-slate-100">
                    Choose a new password
                </h1>
                {done ? (
                    <div className="mt-6">
                        <div
                            data-testid="reset-success"
                            className="rounded-md border border-emerald-500/30 bg-emerald-950/40 px-4 py-3 font-mono text-xs text-emerald-300"
                        >
                            Password updated.
                        </div>
                        <Link
                            to="/login"
                            data-testid="reset-login-link"
                            className="mt-5 block rounded-md bg-cyan-400 px-4 py-2.5 text-center text-xs font-bold text-slate-950 hover:bg-cyan-300"
                        >
                            Log in
                        </Link>
                    </div>
                ) : (
                    <form onSubmit={submit} className="mt-6 space-y-4">
                        <input
                            data-testid="reset-password-input"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="New password (8+ characters)"
                            minLength={8}
                            required
                            className="w-full rounded-md border border-slate-800 bg-slate-950/70 px-4 py-2.5 font-mono text-xs text-slate-200 placeholder:text-slate-600 focus:border-cyan-500/60 focus:outline-none"
                        />
                        <input
                            data-testid="reset-confirm-input"
                            type="password"
                            value={confirm}
                            onChange={(e) => setConfirm(e.target.value)}
                            placeholder="Confirm new password"
                            minLength={8}
                            required
                            className="w-full rounded-md border border-slate-800 bg-slate-950/70 px-4 py-2.5 font-mono text-xs text-slate-200 placeholder:text-slate-600 focus:border-cyan-500/60 focus:outline-none"
                        />
                        {error && (
                            <p data-testid="reset-error" className="font-mono text-[11px] text-rose-400">
                                {error}
                            </p>
                        )}
                        <button
                            data-testid="reset-submit-button"
                            type="submit"
                            disabled={busy || !token}
                            className="w-full rounded-md bg-cyan-400 px-4 py-2.5 text-xs font-bold text-slate-950 hover:bg-cyan-300 disabled:opacity-50"
                        >
                            {busy ? "Updating…" : "Update password"}
                        </button>
                        {!token && (
                            <p className="font-mono text-[11px] text-amber-400">
                                This link is missing its token — request a fresh reset email.
                            </p>
                        )}
                    </form>
                )}
            </div>
        </div>
    );
}
